import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';

export type OptimizationStatus = 'in_progress' | 'completed' | 'error' | 'infeasible';

export interface OptimizationStatusResponse {
    status: OptimizationStatus;
    job_id: string;
    message?: string;
    optimized_plan_id?: string;
}

/**
 * Each tier defines a polling interval for a window of elapsed time.
 * The tiers are evaluated top-to-bottom; the first tier whose `untilMs`
 * has NOT been exceeded is used. The last tier applies indefinitely.
 *
 * Adjust these values freely — no other code changes required.
 */
interface PollingTier {
    /** Upper bound of elapsed time (in ms) for this tier. Use Infinity for the final tier. */
    untilMs: number;
    /** Polling interval (in ms) while within this tier. */
    intervalMs: number;
}

const POLLING_TIERS: PollingTier[] = [
    { untilMs: 60_000, intervalMs: 2_000 },       // 0–1 min  → every 2s
    { untilMs: 360_000, intervalMs: 15_000 },      // 1–6 min  → every 15s
    { untilMs: 960_000, intervalMs: 60_000 },      // 6–16 min → every 60s
    { untilMs: Infinity, intervalMs: 120_000 },    // 16+ min  → every 2 min
];

function getPollingInterval(elapsedMs: number): number {
    for (const tier of POLLING_TIERS) {
        if (elapsedMs < tier.untilMs) {
            return tier.intervalMs;
        }
    }
    return POLLING_TIERS[POLLING_TIERS.length - 1].intervalMs;
}

const fetchOptimizationStatus = async (
    jobId: string,
    signal?: AbortSignal,
): Promise<OptimizationStatusResponse> => {
    const response = await axios(tpoGenUrls.getOptimizationStatus(jobId), {
        method: 'GET',
        signal,
    });
    return response.data;
};

interface UseOptimizationPollingProps {
    jobId: string | null;
    onCompleted?: (optimizedPlanId: string | null) => void;
    onError?: () => void;
    onInfeasible?: (message: string | null) => void;
}

const useOptimizationPolling = ({ jobId, onCompleted, onError, onInfeasible }: UseOptimizationPollingProps) => {
    const pollingStartRef = useRef<number | null>(null);

    if (jobId && pollingStartRef.current == null) {
        pollingStartRef.current = Date.now();
    }
    if (!jobId) {
        pollingStartRef.current = null;
    }

    const { data, isLoading, isError } = useQuery({
        queryKey: tpoGenKeys.optimizationStatus(jobId!),
        queryFn: ({ signal }) => fetchOptimizationStatus(jobId!, signal),
        enabled: Boolean(jobId),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (status !== 'in_progress') return false;

            const elapsed = Date.now() - (pollingStartRef.current ?? Date.now());
            return getPollingInterval(elapsed);
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const status = data?.status ?? null;
    const polledOptimizedPlanId = data?.optimized_plan_id ?? null;
    const polledMessage = data?.message ?? null;

    const onCompletedRef = useRef(onCompleted);
    const onErrorRef = useRef(onError);
    const onInfeasibleRef = useRef(onInfeasible);
    onCompletedRef.current = onCompleted;
    onErrorRef.current = onError;
    onInfeasibleRef.current = onInfeasible;

    const prevStatusRef = useRef(status);
    useEffect(() => {
        if (prevStatusRef.current === status) return;
        prevStatusRef.current = status;
        if (status === 'completed') onCompletedRef.current?.(polledOptimizedPlanId);
        else if (status === 'error') onErrorRef.current?.();
        else if (status === 'infeasible') onInfeasibleRef.current?.(polledMessage);
    }, [status, polledOptimizedPlanId, polledMessage]);

    return {
        optimizationStatus: data?.status ?? null,
        optimizationMessage: data?.message ?? null,
        optimizedPlanId: data?.optimized_plan_id ?? null,
        optimizationPollingLoading: isLoading,
        optimizationPollingError: isError,
    };
};

export default useOptimizationPolling;
